import React, { useCallback } from "react";
import { ThirdPartServiceProviderInfoRow } from "../../../../../../src/api/fmr1";
import { useAppDispatch, useAppSelector } from "../../../../../../src/app/store";
import { addTpspInfoRow, selectFMR1DraftTpspInfoRows, updateTpspInfoRow } from "../../../logic/frm1Slice";
import { generateRowId, YES_NO_OPTIONS } from "../common/utils";
import { FmrSingleRowGridTable } from "../common/FmrSingleRowGridTable";

const PARTICULARS_COL_WIDTH = "253px";
const MIN_DATA_COL_WIDTH = "85px";
const TPSP_COL_WIDTH = "443px";
const IBA_COL_WIDTH = "250px";
const REGISTRATION_COL_WIDTH = "340px";

const buildEmptyRow = (): ThirdPartServiceProviderInfoRow => ({
  row_id: generateRowId(),
  isDirty: true,
  isAdded: true,
});

const TpspDetailsTable: React.FC = () => {
  const dispatch = useAppDispatch();

  const rows = useAppSelector(selectFMR1DraftTpspInfoRows);

  const row0 = rows[0];

  const tpsp = row0?.third_party_service_provider ?? "";
  const iba = row0?.tpsp_reported_to_iba_or_other_bodies ?? "";
  const registration = row0?.tpsp_registration_details ?? "";

  const patchRow = useCallback(
    (changes: Partial<ThirdPartServiceProviderInfoRow>) => {
      if (!rows.length) {
        dispatch(addTpspInfoRow({ row: { ...buildEmptyRow(), ...changes } }));
      } else if (row0) {
        dispatch(updateTpspInfoRow({ id: row0.row_id, changes }));
      }
    },
    [dispatch, row0, rows.length],
  );

  /** Design border + min size on registration column; bottom-right radius comes from `FmrSingleRowGridTable` on `<td>`. */
  const colLastClass =
    "min-h-[56px] min-w-[85px]";

  return (
    <FmrSingleRowGridTable
      title="Third Party Service Provider(TPSP) Details"
      particularsColWidth={PARTICULARS_COL_WIDTH}
      columns={[
        {
          headerName: "Third Party Service Providers (TPSP)**",
          type: "select",
          options: YES_NO_OPTIONS,
          value: tpsp,
          onChange: (v) => patchRow({ third_party_service_provider: v as string }),
          isEditable: true,
          colWidth: TPSP_COL_WIDTH,
          minColWidth: MIN_DATA_COL_WIDTH,
        },
        {
          headerName: "If TPSP is involved, whether reported to IBA or other bodies",
          type: "select",
          options: YES_NO_OPTIONS as any,
          value: iba,
          onChange: (v) => patchRow({ tpsp_reported_to_iba_or_other_bodies: v as string }),
          isEditable: true,
          colWidth: IBA_COL_WIDTH,
          minColWidth: MIN_DATA_COL_WIDTH,
        },
        {
          headerName:
            "If TPSP is involved, registration number or other details of TPSP as reported to IBA or other bodies",
          type: "text",
          value: registration,
          onChange: (v) => patchRow({ tpsp_registration_details: v as string }),
          isEditable: true,
          colWidth: REGISTRATION_COL_WIDTH,
          minColWidth: MIN_DATA_COL_WIDTH,
          headerClassName: colLastClass,
          textCellProps: {
            numberOfRows: 1,
          },
        },
      ]}
    />
  );
};

TpspDetailsTable.displayName = "TpspDetailsTable";

export default TpspDetailsTable;
